import clsx from 'clsx';
import {
  ArrowLeft,
  ArrowUpDown,
  CheckCircle,
  Clock,
  Copy,
  HelpCircle,
  History,
  Play,
  RefreshCw,
  Repeat,
  Save,
  Search,
  ShieldCheck,
  Unlock,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchJSON } from '../api';
import { apiPath } from '../constants';
import { loadDraftForOperator } from '../drafts';
import { useStore } from '../store';
import { showToast } from '../toast';
import type { EpisodeDebugResponse, EpisodeSummary } from '../types';
import { HelpModal } from './HelpModal';
import { ReleaseLeaseModal } from './ReleaseLeaseModal';

type ActiveTab = 'all' | 'voting' | 'completed' | 'audited';
type HistorySort = 'recent' | 'id';

function timeAgo(ms: number | null | undefined): string {
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 0) return '';
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function OperatorDashboard() {
  const {
    operator,
    operatorDashboardEpisodes,
    statsCache,
    myStatsCache,
    isLoading,
    showUserSelection,
    startOperatorRandom,
    openEpisode,
    lookupEpisode,
    loadOperatorHistory,
    fetchStatsData,
    fetchMyStatsData,
    showViewerLoadingShell,
    authUser,
    isAdmin,
    isAuditor,
    operatorAutoRefresh,
    setOperatorAutoRefresh,
  } = useStore();

  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [historySearch, setHistorySearch] = useState('');
  const [historySort, setHistorySort] = useState<HistorySort>('recent');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);
  const features = myStatsCache?.features ?? statsCache?.features;
  const manualLeaseRelease = features?.manual_lease_release === true;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleRefresh = useCallback(async () => {
    const statsTasks: Promise<unknown>[] = [fetchMyStatsData(true)];
    if (isAdmin) statsTasks.push(fetchStatsData(true));
    await Promise.all([loadOperatorHistory(), ...statsTasks]);
    setLastUpdated(new Date());
  }, [loadOperatorHistory, fetchStatsData, fetchMyStatsData, isAdmin]);

  useEffect(() => {
    void handleRefresh();
  }, [handleRefresh]);

  useEffect(() => {
    if (!operatorAutoRefresh) return;
    const interval = setInterval(() => {
      void handleRefresh();
    }, 60000);
    return () => clearInterval(interval);
  }, [operatorAutoRefresh, handleRefresh]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const isInput = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;

      if (e.key === '?' && !isInput) {
        e.preventDefault();
        setIsHelpOpen((prev) => !prev);
      }

      if (e.key === '/' && !isInput && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      if (isInput) return;

      if (e.key.toLowerCase() === 'r') {
        void handleRefresh();
        showToast('Refreshing...');
      }
      if (e.key === 'Enter' && operator) {
        if (isAuditor && !isAdmin) {
          void useStore.getState().startAuditRandom(operator);
        } else {
          void startOperatorRandom(operator);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [operator, handleRefresh, startOperatorRandom, isAuditor, isAdmin]);

  const myStats =
    myStatsCache && myStatsCache.operator === operator
      ? myStatsCache
      : statsCache?.operators.find((op) => op.name === operator);
  const isMax = authUser?.user === 'max';
  const showSwitchUser = isMax || isAdmin;

  const draftEpisodes = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!operator) return map;
    operatorDashboardEpisodes.forEach((ep) => {
      if (loadDraftForOperator(operator, ep.id)) {
        map.set(ep.id, true);
      }
    });
    return map;
  }, [operator, operatorDashboardEpisodes]);

  const historyTotals = useMemo(() => {
    const total = operatorDashboardEpisodes.length;
    const completed = operatorDashboardEpisodes.filter(
      (ep) => ep.mark_status === 'completed' || ep.mark_status === 'audited'
    ).length;
    const inProgress = total - completed;
    const drafts = draftEpisodes.size;
    const completionRate = total ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, drafts, completionRate };
  }, [operatorDashboardEpisodes, draftEpisodes]);

  const latestDraft = useMemo(
    () => operatorDashboardEpisodes.find((ep) => draftEpisodes.has(ep.id)) ?? null,
    [operatorDashboardEpisodes, draftEpisodes]
  );

  const filteredHistory = useMemo(() => {
    const search = historySearch.trim().toLowerCase();
    let filtered = [...operatorDashboardEpisodes];

    if (search) {
      filtered = filtered.filter((ep) => ep.id.toLowerCase().includes(search));
    }

    if (activeTab === 'voting') {
      filtered = filtered.filter((ep) => ep.mark_status === 'voting');
    } else if (activeTab === 'completed') {
      filtered = filtered.filter((ep) => ep.mark_status === 'completed');
    } else if (activeTab === 'audited') {
      filtered = filtered.filter((ep) => ep.mark_status === 'audited');
    }

    if (historySort === 'id') {
      filtered.sort((a, b) => a.id.localeCompare(b.id));
    }

    return filtered;
  }, [historySearch, activeTab, historySort, operatorDashboardEpisodes]);

  const openHistoryEpisode = async (ep: EpisodeSummary) => {
    if (ep.audit_draft_operator === operator && features?.auditor_amend_confirm === true) {
      await lookupEpisode(ep.id, {
        autoOpen: true,
        showViewerPlaceholder: true,
        intent: 'audit-claim',
        claimFailureReadOnly: true,
      });
      return;
    }
    if (ep.mark_status === 'voting' && features?.annotator_amend_confirm === true) {
      await lookupEpisode(ep.id, {
        autoOpen: true,
        showViewerPlaceholder: true,
        intent: 'operator-claim',
        claimFailureReadOnly: true,
      });
      return;
    }
    showViewerLoadingShell(ep.id);
    if (ep.mark_status !== 'audited') {
      await openEpisode(ep.id, ep.device_type_enum ?? null);
      return;
    }
    try {
      const params = new URLSearchParams({ operator });
      const review = await fetchJSON<EpisodeDebugResponse>(
        apiPath(`/me/review/${encodeURIComponent(ep.id)}?${params}`)
      );
      await openEpisode(ep.id, ep.device_type_enum ?? null, review);
    } catch (err) {
      console.error('Failed to open self-review', err);
      showToast('Review is not available for this case');
      await handleRefresh();
    }
  };

  if (myStatsCache === null && statsCache === null) {
    return (
      <div className="flex items-center justify-center py-32 gap-3 text-brand-text-secondary">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} context="dashboard" />
      {manualLeaseRelease && (
        <ReleaseLeaseModal isOpen={isReleaseOpen} onClose={() => setIsReleaseOpen(false)} />
      )}

      {/* Manual lease-release escape hatch (manual_lease_release feature flag) */}
      {manualLeaseRelease && (
        <div className="flex flex-col gap-3 rounded-lg border border-brand-border bg-brand-surface/50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-brand-text-secondary">
            Episode won't load or stuck on one you can't work on? Release your lease and pick up a
            new one.
          </p>
          <button
            type="button"
            onClick={() => setIsReleaseOpen(true)}
            className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:border-brand-orange/50 hover:text-white"
            title="Release my annotate or audit lease"
          >
            <Unlock className="h-3.5 w-3.5" /> Release lease
          </button>
        </div>
      )}

      {/* Max Warning Banner */}
      {isMax && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4 text-amber-500 text-sm font-medium">
          max用户将很快被禁用，请尽快切换到独立用户名({operator}
          )。您的标注历史和进度将被继承，如果您有多个用户名，请联系管理员将他们合并。
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-brand-border pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-brand-dark border border-brand-border flex items-center justify-center text-lg text-brand-orange">
              {operator.charAt(0).toUpperCase()}
            </div>
            Welcome, {operator}
          </h1>
          <p className="mt-2 text-brand-text-secondary">Your annotation workspace and history.</p>
        </div>

        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-brand-text-secondary">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-2 text-xs text-brand-text-secondary">
            <input
              type="checkbox"
              checked={operatorAutoRefresh}
              onChange={(e) => setOperatorAutoRefresh(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-brand-border bg-brand-dark text-brand-orange focus:ring-brand-orange"
            />
            Auto refresh
          </label>
          <button
            type="button"
            onClick={() => void useStore.getState().showQualityFeedback()}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
            title="Released calibration results"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Quality
          </button>
          <button
            type="button"
            onClick={() => setIsHelpOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
            title="Shortcuts (?)"
          >
            <HelpCircle className="h-3.5 w-3.5" /> Help
          </button>
          {showSwitchUser && (
            <button
              type="button"
              onClick={() => void showUserSelection()}
              className="flex items-center gap-2 rounded-lg border border-brand-border bg-transparent px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Switch User
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="rounded-xl border border-brand-border bg-brand-surface p-6">
          <div className="flex items-center gap-3 text-brand-text-secondary mb-2">
            <CheckCircle className="h-5 w-5 text-emerald-500" />
            <span className="text-xs uppercase font-bold tracking-wider">Episodes Done</span>
          </div>
          <div className="text-3xl font-bold text-white font-mono">
            {myStats?.episodes.toLocaleString() ?? 0}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-6">
          <div className="flex items-center gap-3 text-brand-text-secondary mb-2">
            <Clock className="h-5 w-5 text-brand-orange" />
            <span className="text-xs uppercase font-bold tracking-wider">Frames Labeled</span>
          </div>
          <div className="text-3xl font-bold text-white font-mono">
            {myStats?.images.toLocaleString() ?? 0}
          </div>
        </div>
      </div>

      {/* History Snapshot */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-bold tracking-wider text-brand-text-secondary">
            History
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {historyTotals.total.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-bold tracking-wider text-brand-text-secondary">
            In Progress
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {historyTotals.inProgress.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-bold tracking-wider text-brand-text-secondary">
            Drafts
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {historyTotals.drafts.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs uppercase font-bold tracking-wider text-brand-text-secondary">
            Completion
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {historyTotals.completionRate}%
          </div>
        </div>
      </div>

      {/* Main Action */}
      <div className="flex flex-wrap justify-center gap-3 py-4">
        <button
          type="button"
          onClick={() => void startOperatorRandom(operator)}
          className="group relative flex items-center gap-3 rounded-full bg-brand-orange px-8 py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 hover:bg-brand-orange-hover"
        >
          <Play className="fill-current" />
          Start to Annotate
        </button>
        {(isAuditor || isAdmin) && (
          <button
            type="button"
            onClick={() => void useStore.getState().startAuditRandom(operator)}
            className="group relative flex items-center gap-3 rounded-full bg-emerald-500 px-8 py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 hover:bg-emerald-600"
          >
            <ShieldCheck className="h-5 w-5" />
            Start to Audit
          </button>
        )}
        {latestDraft && (
          <button
            type="button"
            onClick={() => void openEpisode(latestDraft.id, latestDraft.device_type_enum ?? null)}
            className="group relative flex items-center gap-3 rounded-full border border-brand-border bg-brand-surface px-6 py-4 text-sm font-semibold text-brand-text-primary transition hover:border-brand-orange hover:text-white"
            title={`Resume ${latestDraft.id}`}
          >
            <Repeat className="h-4 w-4" />
            Resume Draft
          </button>
        )}
      </div>

      {/* Recent History */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <History className="h-5 w-5 text-brand-text-secondary" />
              History
            </h2>

            <div className="flex p-1 bg-brand-dark border border-brand-border rounded-lg">
              {(['all', 'voting', 'completed', 'audited'] as ActiveTab[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={clsx(
                    'px-4 py-1.5 rounded-md text-xs font-medium transition-colors capitalize',
                    activeTab === tab
                      ? 'bg-brand-surface text-white shadow-sm'
                      : 'text-brand-text-secondary hover:text-white'
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => void handleRefresh()}
            className="p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white transition-colors"
            title="Refresh History"
          >
            <RefreshCw className={clsx('h-4 w-4', isLoading && 'animate-spin')} />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand-border bg-brand-surface p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-text-secondary" />
              <input
                ref={searchInputRef}
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Filter by ID (/)"
                className="h-9 w-40 rounded-lg border border-brand-border bg-brand-dark pl-8 pr-3 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
              />
            </div>

            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-3.5 w-3.5 text-brand-text-secondary" />
              <select
                value={historySort}
                onChange={(e) => setHistorySort(e.target.value as HistorySort)}
                className="rounded-lg border border-brand-border bg-brand-dark px-2 py-1 text-xs text-brand-text-primary focus:border-brand-orange focus:outline-none"
              >
                <option value="recent">Recent</option>
                <option value="id">ID</option>
              </select>
            </div>
          </div>

          <div className="text-xs text-brand-text-secondary font-medium">
            Showing {filteredHistory.length.toLocaleString()} of{' '}
            {operatorDashboardEpisodes.length.toLocaleString()}
          </div>
        </div>

        {filteredHistory.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredHistory.map((ep) => {
              const status = ep.mark_status ?? 'pending';
              const hasDraft = draftEpisodes.has(ep.id);
              const hasAuditDraft = ep.audit_draft_operator === operator;
              const voteCount = Math.max(0, ep.operator_count || 0);
              const requiredVotes = myStatsCache?.required_votes ?? statsCache?.required_votes ?? 2;
              const progressRatio =
                status === 'audited' || status === 'completed'
                  ? 1
                  : Math.min(1, voteCount / requiredVotes);
              const progressPercent = Math.round(progressRatio * 100);

              let statusIcon = (
                <div className="h-2 w-2 rounded-full bg-brand-orange shrink-0 mt-1" />
              );
              let statusText = 'Pending';
              let cardBorderClass =
                'border-brand-border bg-brand-surface hover:border-brand-orange/30';

              if (status === 'completed') {
                statusIcon = <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />;
                statusText = hasAuditDraft ? 'Audit awaiting confirmation' : 'Completed';
                cardBorderClass =
                  'border-brand-border bg-brand-surface hover:border-emerald-500/30';
              } else if (status === 'audited') {
                statusIcon = <ShieldCheck className="h-4 w-4 text-blue-500 shrink-0" />;
                statusText = 'Audited';
                cardBorderClass = 'border-brand-border bg-brand-surface hover:border-blue-500/30';
              } else if (status === 'voting') {
                statusIcon = <Users className="h-4 w-4 text-purple-500 shrink-0" />;
                statusText = 'Voting';
                cardBorderClass = 'border-brand-border bg-brand-surface hover:border-purple-500/30';
              }

              return (
                <div
                  key={ep.id}
                  className={clsx(
                    'group flex flex-col justify-between gap-4 rounded-xl border p-5 transition-all hover:-translate-y-1 hover:shadow-lg',
                    cardBorderClass
                  )}
                >
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="text-sm font-mono font-bold text-brand-text-primary truncate">
                          {ep.id}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigator.clipboard.writeText(ep.id);
                            showToast('Episode ID copied');
                          }}
                          className="p-1 text-brand-text-secondary hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                          title="Copy ID"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                      {statusIcon}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-brand-text-secondary capitalize">
                        {statusText}
                        {ep.last_updated_ms && !hasDraft && (
                          <span className="ml-2 text-[10px] opacity-60">
                            · {timeAgo(ep.last_updated_ms)}
                          </span>
                        )}
                      </span>
                      {hasDraft && (
                        <span
                          className="flex items-center gap-1 text-[10px] text-brand-orange bg-brand-orange/10 px-1.5 py-0.5 rounded border border-brand-orange/20"
                          title="Unsaved Draft"
                        >
                          <Save className="h-3 w-3" />
                          <span>Draft</span>
                        </span>
                      )}
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-brand-text-secondary">
                        <span>Progress</span>
                        <span>
                          {voteCount}/{requiredVotes} · {progressPercent}%
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-brand-dark">
                        <div
                          className={clsx(
                            'h-1.5 rounded-full transition-all',
                            status === 'audited'
                              ? 'bg-blue-400'
                              : status === 'completed'
                                ? 'bg-emerald-400'
                                : status === 'voting'
                                  ? 'bg-purple-400'
                                  : 'bg-brand-orange'
                          )}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      void openHistoryEpisode(ep);
                    }}
                    className="w-full rounded-lg border border-brand-border bg-brand-dark px-3 py-2 text-xs font-semibold text-brand-text-secondary transition-colors group-hover:bg-brand-orange group-hover:text-white group-hover:border-transparent"
                  >
                    {hasAuditDraft ? 'Edit audit' : 'Review'}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface/30 p-12 text-center text-brand-text-secondary">
            No matching history found. Adjust filters or start annotating to see your work here!
          </div>
        )}
      </div>
    </div>
  );
}
